import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, X, Scissors, Calendar as CalIcon, User } from "lucide-react";
import api from "@/lib/api";

const MAX_POLLS = 15;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const pollRef = useRef(0);
  const [appt, setAppt] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("tbc_pending_appt");
      if (raw) setAppt(JSON.parse(raw));
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setState("paid"); sessionStorage.removeItem("tbc_pending_appt"); return; }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setState("error"); return; }
        pollRef.current += 1;
        if (pollRef.current >= MAX_POLLS) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-[#d4af37] animate-spin mx-auto mb-6" />
            <p className="overline text-xs text-white/60">Confirming your payment…</p>
          </>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring" }} className="w-20 h-20 border-2 border-[#d4af37] rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="w-9 h-9 text-[#d4af37]" strokeWidth={2} />
            </motion.div>
            <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">Payment <span className="italic text-[#d4af37]">received.</span></h1>
            <p className="text-white/50 font-body mb-10">You're all booked and paid. See you in the chair!</p>
            {appt && (
              <div className="border border-white/10 p-6 bg-[#0a0a0a] text-left mb-10">
                <Row icon={Scissors} label={appt.service_name} value={`$${appt.service_price}`} />
                <Row icon={CalIcon} label={new Date(appt.date + "T00:00:00").toDateString()} value={appt.time_slot} />
                <Row icon={User} label={appt.customer_name} value={appt.customer_phone} />
              </div>
            )}
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => navigate("/")} className="border border-white/20 px-8 py-3.5 overline text-xs text-white hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid="success-home-btn">Back Home</button>
              <button onClick={() => navigate("/account")} className="bg-[#d4af37] text-black px-8 py-3.5 overline text-xs hover:bg-[#c5a028] transition-colors" data-testid="success-bookings-btn">My Bookings</button>
            </div>
          </motion.div>
        )}

        {(state === "timeout") && (
          <>
            <Loader2 className="w-10 h-10 text-[#d4af37] mx-auto mb-6" />
            <h1 className="font-display text-3xl text-white mb-3">Almost there</h1>
            <p className="text-white/50 font-body mb-8">Your payment is still processing. Your appointment is saved — check "My Bookings" shortly.</p>
            <Link to="/account" className="bg-[#d4af37] text-black px-8 py-3.5 overline text-xs">My Bookings</Link>
          </>
        )}

        {state === "error" && (
          <>
            <div className="w-16 h-16 border-2 border-red-400/50 rounded-full flex items-center justify-center mx-auto mb-6"><X className="w-8 h-8 text-red-400" /></div>
            <h1 className="font-display text-3xl text-white mb-3">Something went wrong</h1>
            <p className="text-white/50 font-body mb-8">We couldn't confirm this payment. Your appointment may still be held — please contact the shop or try again.</p>
            <Link to="/book" className="border border-[#d4af37] text-[#d4af37] px-8 py-3.5 overline text-xs hover:bg-[#d4af37] hover:text-black transition-colors">Back to Booking</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 flex items-center justify-center" data-testid="payment-cancel-page">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 border-2 border-white/20 rounded-full flex items-center justify-center mx-auto mb-6"><X className="w-8 h-8 text-white/60" /></div>
        <h1 className="font-display text-3xl text-white mb-3">Payment cancelled</h1>
        <p className="text-white/50 font-body mb-8">No charge was made. Your slot may still be held — you can complete payment later or pay at the shop.</p>
        <div className="flex items-center justify-center gap-4">
          <Link to="/" className="border border-white/20 px-8 py-3.5 overline text-xs text-white hover:border-[#d4af37] hover:text-[#d4af37] transition-colors">Back Home</Link>
          <Link to="/book" className="bg-[#d4af37] text-black px-8 py-3.5 overline text-xs hover:bg-[#c5a028] transition-colors">Try Again</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3 text-white/70 font-body text-sm"><Icon className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} /> {label}</div>
      <span className="text-white font-body text-sm">{value}</span>
    </div>
  );
}
